import React from 'react';
import { useTranslation } from 'react-i18next';

import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';

import { useFilterContext } from '../../../../FilterContext';
import { FILTRATION_ICONS } from '../../../../utils/constants';
import { getOppositeLikeOperation } from '../../../../utils/helpers';
import {
  FILTER_OPERATIONS,
  IAdditionalCondition,
  ISelectFilterProps,
} from '../../../../utils/types';
import { StyledButton } from '../../FilterOperationButton/styled';
import FilterComponent, { IFilterComponent } from '../FilterComponent';
import { ConditionBlock, ConditionToggle, ConflictHint, FilterElements } from '../styled';

interface IAdditionalConditionProps
  extends Pick<
    IFilterComponent,
    'isRequestFilter' | 'isSelect' | 'onToggleFilterValue' | 'type' | 'variant'
  > {
  additionalCondition?: IAdditionalCondition;
  columnKey: string;
  currentOperation: FILTER_OPERATIONS;
  isMultiSelect: boolean;
  listOperations: FILTER_OPERATIONS[];
  onSelectColumnFilter?: (props: ISelectFilterProps) => void;
  onToggle?: () => void;
}

const AdditionalCondition = ({
  additionalCondition,
  columnKey,
  currentOperation,
  isMultiSelect,
  isRequestFilter,
  isSelect,
  listOperations,
  onSelectColumnFilter,
  onToggle,
  onToggleFilterValue,
  type,
  variant,
}: IAdditionalConditionProps) => {
  const { t } = useTranslation();

  const {
    config: { fallbackValues },
    conflictColumns,
    toggleAdditionalCondition,
  } = useFilterContext();

  const onToggleAdditionalCondition = React.useCallback(() => {
    toggleAdditionalCondition({ columnKey, operation: currentOperation });
    onToggle?.();
  }, [toggleAdditionalCondition, columnKey, currentOperation, onToggle]);

  const isLikeOperation =
    currentOperation === FILTER_OPERATIONS.LIKE ||
    currentOperation === FILTER_OPERATIONS.NOT_LIKE;
  const canAddCondition =
    isLikeOperation &&
    listOperations.includes(FILTER_OPERATIONS.LIKE) &&
    listOperations.includes(FILTER_OPERATIONS.NOT_LIKE) &&
    !fallbackValues?.[columnKey];

  if (!canAddCondition) {
    return null;
  }

  const additionalOperation =
    additionalCondition?.operation || getOppositeLikeOperation(currentOperation);
  const hasConflict = conflictColumns.includes(columnKey);

  return (
    <>
      <ConditionToggle
        checked={!!additionalCondition}
        hintText={t('common.filters.addConditionHint')}
        label={t(
          currentOperation === FILTER_OPERATIONS.LIKE
            ? 'common.filters.addNotLikeCondition'
            : 'common.filters.addLikeCondition',
        )}
        onChange={onToggleAdditionalCondition}
        testIds={{ container: `${columnKey}AddConditionSwitch` }}
      />
      {additionalCondition && (
        <ConditionBlock>
          <FilterElements>
            <StyledButton
              $enableShowFocus
              disabled
              iconSize={19}
              startIcon={FILTRATION_ICONS[additionalOperation]}
              testIds={{ container: `${columnKey}AdditionalOperationButton` }}
              variant='icon'
            />
            <FilterComponent
              appliedFilter={additionalCondition}
              columnKey={columnKey}
              fallbackOperation={additionalOperation}
              isAdditional
              isRequestFilter={isRequestFilter}
              isSelect={isSelect}
              multiSelect={isMultiSelect}
              onSelectColumnFilter={onSelectColumnFilter}
              onToggleFilterValue={onToggleFilterValue}
              type={type}
              variant={variant}
            />
          </FilterElements>
        </ConditionBlock>
      )}
      {hasConflict && (
        <ConflictHint data-testid={`${columnKey}ConflictHint`}>
          <ICONS_MAP.DangerExclamation />
          {t('common.filters.sameValueInBothConditions')}
        </ConflictHint>
      )}
    </>
  );
};

export default appReactMemo(AdditionalCondition);
